import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import CalendarPoster from './CalendarPoster';
import {
  ShippingCalendarConfig,
  DayMark,
  MarkType,
  PresetEntry,
  MARK_TYPE_LABELS,
  MARK_TYPE_DEFAULT_LABEL,
  createEmptyConfig,
  loadConfig,
  saveConfig,
  clearConfig,
  loadPresets,
  savePresets,
  newPresetId,
  snapConfig,
  upsertMark,
  removeMark,
  findMark,
  upsertExtraLabel,
  removeExtraLabel,
} from './config';

const MARK_TYPES: MarkType[] = ['ship', 'partial', 'noship'];

function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return ((...args: any[]) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

function sanitizeFilename(s: string): string {
  return s.replace(/[\/\\?%*:|"<>\s]+/g, '_');
}

export default function ShippingCalendar() {
  const [config, setConfig] = useState<ShippingCalendarConfig>(() => loadConfig() ?? createEmptyConfig());
  const [presets, setPresets] = useState<PresetEntry[]>(() => loadPresets());
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const [editingDate, setEditingDate] = useState<string>('');
  const [toast, setToast] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [newLabelDate, setNewLabelDate] = useState<string>('');
  const [newLabelText, setNewLabelText] = useState<string>('');

  const posterRef = useRef<HTMLDivElement | null>(null);

  const debouncedSave = useMemo(() => debounce((c: ShippingCalendarConfig) => saveConfig(c), 500), []);

  useEffect(() => {
    debouncedSave(config);
  }, [config, debouncedSave]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const updateField = <K extends keyof ShippingCalendarConfig>(key: K, value: ShippingCalendarConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleStartDateChange = (iso: string) => {
    setConfig(prev => snapConfig({ ...prev, startDate: iso }));
  };

  const handleEndDateChange = (iso: string) => {
    setConfig(prev => snapConfig({ ...prev, endDate: iso }));
  };

  const handleCellClick = (date: string) => {
    setEditingDate(date);
    const existing = findMark(config.marks, date);
    if (!existing) {
      setConfig(prev => ({ ...prev, marks: upsertMark(prev.marks, { date, type: 'ship', label: MARK_TYPE_DEFAULT_LABEL.ship }) }));
    }
  };

  const editingMark = editingDate ? findMark(config.marks, editingDate) : undefined;

  const updateEditingMark = (patch: Partial<DayMark>) => {
    if (!editingMark) return;
    const next: DayMark = { ...editingMark, ...patch };
    // 유형 변경 시 라벨이 다른 유형의 기본 라벨이거나 비어있으면 새 유형의 기본 라벨로 자동 교체
    if (patch.type && patch.type !== editingMark.type) {
      const oldDefault = MARK_TYPE_DEFAULT_LABEL[editingMark.type];
      const newDefault = MARK_TYPE_DEFAULT_LABEL[patch.type];
      if (!editingMark.label.trim() || editingMark.label === oldDefault) {
        next.label = newDefault;
      }
    }
    setConfig(prev => ({ ...prev, marks: upsertMark(prev.marks, next) }));
  };

  const deleteEditingMark = () => {
    if (!editingDate) return;
    setConfig(prev => ({ ...prev, marks: removeMark(prev.marks, editingDate) }));
    setEditingDate('');
  };

  const addExtraLabel = () => {
    const date = newLabelDate.trim();
    const label = newLabelText.trim();
    if (!date || !label) return;
    setConfig(prev => ({ ...prev, extraLabels: upsertExtraLabel(prev.extraLabels, { date, label }) }));
    setNewLabelDate('');
    setNewLabelText('');
    showToast('라벨이 추가되었습니다');
  };

  const deleteExtraLabel = (date: string) => {
    setConfig(prev => ({ ...prev, extraLabels: removeExtraLabel(prev.extraLabels, date) }));
  };

  const savePreset = () => {
    const name = window.prompt('프리셋 이름');
    if (!name || !name.trim()) return;
    const entry: PresetEntry = {
      id: newPresetId(),
      name: name.trim(),
      config,
      savedAt: new Date().toISOString(),
    };
    const next = [...presets, entry];
    setPresets(next);
    savePresets(next);
    setSelectedPresetId(entry.id);
    showToast('프리셋이 저장되었습니다');
  };

  const loadPreset = (id: string) => {
    setSelectedPresetId(id);
    if (!id) return;
    const entry = presets.find(p => p.id === id);
    if (entry) setConfig(entry.config);
  };

  const deletePreset = () => {
    if (!selectedPresetId) return;
    if (!window.confirm('선택한 프리셋을 삭제할까요?')) return;
    const next = presets.filter(p => p.id !== selectedPresetId);
    setPresets(next);
    savePresets(next);
    setSelectedPresetId('');
    showToast('프리셋이 삭제되었습니다');
  };

  const resetForm = () => {
    if (!window.confirm('현재 편집 내용을 초기화할까요? (프리셋은 보존됩니다)')) return;
    clearConfig();
    setConfig(createEmptyConfig());
    setEditingDate('');
    setSelectedPresetId('');
  };

  const downloadImage = async () => {
    if (!posterRef.current) return;
    setIsExporting(true);
    try {
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      await document.fonts.ready;
      const dataUrl = await toPng(posterRef.current, { pixelRatio: 8, backgroundColor: '#ffffff', cacheBust: true });
      const titlePart = sanitizeFilename(config.title.slice(0, 10));
      const filename = `배송일정_${titlePart}_${config.startDate}_${config.endDate}.png`;
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      a.click();
      showToast('이미지가 저장되었습니다');
    } catch (e) {
      alert('이미지 저장 실패: ' + (e as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown';

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-800 mb-6">배송 달력</h1>
      <div className="flex gap-6">
        {/* 설정 패널 */}
        <div className="w-[400px] space-y-4 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">프리셋</h3>
            <div className="flex gap-2">
              <select
                value={selectedPresetId}
                onChange={e => loadPreset(e.target.value)}
                className={inputCls}
              >
                <option value="">불러오기...</option>
                {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button onClick={savePreset} className="px-3 py-2 bg-jeju-brown text-white rounded text-sm whitespace-nowrap">저장</button>
              <button onClick={deletePreset} disabled={!selectedPresetId} className="px-3 py-2 bg-gray-100 text-gray-600 rounded text-sm disabled:opacity-40">삭제</button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">제목</label>
              <input value={config.title} onChange={e => updateField('title', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">부제</label>
              <textarea value={config.subtitle} onChange={e => updateField('subtitle', e.target.value)} rows={3} className={`${inputCls} font-sans resize-none`} />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">시작일</label>
                <input type="date" value={config.startDate} onChange={e => handleStartDateChange(e.target.value)} className={inputCls} />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">종료일</label>
                <input type="date" value={config.endDate} onChange={e => handleEndDateChange(e.target.value)} className={inputCls} />
              </div>
            </div>
            <p className="text-[11px] text-gray-400">선택한 날짜의 주 일요일/토요일로 자동 스냅됩니다.</p>
            <div>
              <label className="block text-xs text-gray-500 mb-1">연락처</label>
              <input value={config.contactPhone} onChange={e => updateField('contactPhone', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">하단 안내 문구</label>
              <textarea value={config.footerMessage} onChange={e => updateField('footerMessage', e.target.value)} rows={2} className={`${inputCls} font-sans resize-none`} />
            </div>
          </div>

          {editingMark && (
            <div className="bg-white rounded-lg shadow-sm border border-jeju-brown/30 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">날짜 편집 — {editingDate}</h3>
                <button onClick={() => setEditingDate('')} className="text-xs text-gray-400">닫기</button>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">유형</label>
                <div className="flex gap-3 flex-wrap">
                  {MARK_TYPES.map(t => (
                    <label key={t} className="flex items-center gap-1 text-sm">
                      <input type="radio" name="markType" checked={editingMark.type === t} onChange={() => updateEditingMark({ type: t })} />
                      {MARK_TYPE_LABELS[t]}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">라벨</label>
                <input value={editingMark.label} onChange={e => updateEditingMark({ label: e.target.value })} placeholder="예: 삼일절, 출고마감" className={inputCls} />
              </div>
              <button onClick={deleteEditingMark} className="w-full px-3 py-2 bg-red-50 text-red-600 rounded text-sm">이 날짜 마킹 삭제</button>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">추가 라벨 ({config.extraLabels.length})</h3>
            <p className="text-[11px] text-gray-400 -mt-1">자동 공휴일 외에 빨간 라벨로 표시할 날짜 (예: 지방선거, 휴무).</p>
            <div className="flex gap-2">
              <input
                type="date"
                value={newLabelDate}
                onChange={e => setNewLabelDate(e.target.value)}
                className={`${inputCls} flex-shrink-0`}
                style={{ width: '140px' }}
              />
              <input
                value={newLabelText}
                onChange={e => setNewLabelText(e.target.value)}
                placeholder="라벨 (예: 지방선거)"
                className={inputCls}
                onKeyDown={e => { if (e.key === 'Enter') addExtraLabel(); }}
              />
              <button
                onClick={addExtraLabel}
                disabled={!newLabelDate || !newLabelText.trim()}
                className="px-3 py-2 bg-jeju-brown text-white rounded text-sm whitespace-nowrap disabled:opacity-40"
              >추가</button>
            </div>
            {config.extraLabels.length > 0 && (
              <ul className="divide-y divide-gray-100">
                {config.extraLabels.map(l => (
                  <li key={l.date} className="py-2 flex items-center justify-between text-sm">
                    <div>
                      <span className="font-mono text-gray-700">{l.date}</span>
                      <span className="ml-2 text-red-500 font-medium">{l.label}</span>
                    </div>
                    <button onClick={() => deleteExtraLabel(l.date)} className="text-xs text-red-400 ml-2">삭제</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">등록된 마킹 ({config.marks.length})</h3>
            {config.marks.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">달력에서 날짜를 클릭하면 마킹을 추가할 수 있습니다.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {config.marks.map(m => (
                  <li key={m.date} className="py-2 flex items-center justify-between text-sm">
                    <button onClick={() => setEditingDate(m.date)} className="flex-1 text-left hover:underline">
                      <span className="font-mono text-gray-700">{m.date}</span>
                      <span className="ml-2 text-xs text-gray-500">{MARK_TYPE_LABELS[m.type]}</span>
                      {m.label && <span className="ml-2 text-gray-800">{m.label}</span>}
                    </button>
                    <button onClick={() => setConfig(prev => ({ ...prev, marks: removeMark(prev.marks, m.date) }))} className="text-xs text-red-400 ml-2">삭제</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex gap-2">
            <button onClick={downloadImage} className="flex-1 px-4 py-2 bg-jeju-brown text-white rounded font-medium">이미지 저장</button>
            <button onClick={resetForm} className="px-4 py-2 bg-gray-100 text-gray-600 rounded">초기화</button>
          </div>
        </div>

        {/* 미리보기 */}
        <div className="flex-1 overflow-auto">
          <div className="inline-block">
            <CalendarPoster ref={posterRef} config={config} onCellClick={handleCellClick} capture={isExporting} />
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
